import { Link, Outlet, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  CreditCard,
  Globe,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  Plug,
  Scale,
  Settings,
  User,
  UsersRound,
  Webhook,
} from 'lucide-react';

import { ApiError, merchantAuth, type MerchantRole } from '@/api/client';
import { Wordmark } from '@/components/brand/logo';
import { SUPPORTED, dirFor, type LangCode } from '@/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/m')({
  beforeLoad: async ({ context, location }) => {
    if (
      location.pathname === '/m/login' ||
      location.pathname === '/m/forgot-password' ||
      location.pathname === '/m/reset-password' ||
      location.pathname === '/m/verify-email'
    )
      return;
    try {
      await context.queryClient.fetchQuery({
        queryKey: ['merchant-me'],
        queryFn: merchantAuth.me,
        staleTime: 5_000,
      });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        throw redirect({ to: '/m/login' });
      }
      throw err;
    }
  },
  component: MerchantShell,
});

type NavItem = {
  to: string;
  labelKey: string;
  icon: typeof LayoutDashboard;
  // Hidden unless caller's role is in this list. Mirrors the backend
  // matrix in app/core/merchant_rbac.py — UI is cosmetic, server is the
  // source of truth.
  allowedRoles?: MerchantRole[];
};

const ALL: MerchantRole[] = ['owner', 'developer', 'finance', 'viewer'];
const READ_KEYS: MerchantRole[] = ['owner', 'developer', 'finance', 'viewer'];
const WRITE_KEYS: MerchantRole[] = ['owner', 'developer'];

const NAV: NavItem[] = [
  { to: '/m/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard, allowedRoles: ALL },
  { to: '/m/payments', labelKey: 'nav.payments', icon: CreditCard, allowedRoles: ALL },
  { to: '/m/api-keys', labelKey: 'api_keys.title', icon: KeyRound, allowedRoles: READ_KEYS },
  { to: '/m/webhooks', labelKey: 'nav.webhooks', icon: Webhook, allowedRoles: READ_KEYS },
  { to: '/m/settlements', labelKey: 'nav.settlements', icon: Scale, allowedRoles: ALL },
  {
    to: '/m/gateways',
    labelKey: 'merchant.nav.gateways',
    icon: Settings,
    allowedRoles: ['owner', 'developer', 'viewer'],
  },
  {
    to: '/m/integrations',
    labelKey: 'merchant.nav.integrations',
    icon: Plug,
    allowedRoles: ['owner', 'developer', 'viewer'],
  },
  { to: '/m/users', labelKey: 'merchant.nav.users', icon: UsersRound, allowedRoles: ['owner'] },
  { to: '/m/org', labelKey: 'merchant.nav.org', icon: Building2, allowedRoles: ALL },
  { to: '/m/profile', labelKey: 'merchant.nav.profile', icon: User, allowedRoles: ALL },
];

void WRITE_KEYS; // reserved for future write-only nav items

function MerchantShell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ['merchant-me'], queryFn: merchantAuth.me });
  const [navOpen, setNavOpen] = React.useState(false);

  React.useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = dirFor(i18n.language);
  }, [i18n.language]);

  const onLogout = async () => {
    await merchantAuth.logout();
    navigate({ to: '/m/login' });
  };

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="px-2 py-3">
        <Wordmark size="md" />
      </div>
      <nav className="space-y-0.5 flex-1 mt-2 overflow-y-auto">
        {NAV.filter(
          (item) => !item.allowedRoles || (me.data && item.allowedRoles.includes(me.data.role)),
        ).map((item) => (
          <Link
            key={item.to}
            to={item.to}
            onClick={() => setNavOpen(false)}
            className={cn(
              'group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              'data-[status=active]:bg-accent data-[status=active]:text-accent-foreground',
            )}
            activeProps={{ 'data-status': 'active' }}
          >
            <span
              aria-hidden
              className="absolute start-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary opacity-0 transition-opacity group-data-[status=active]:opacity-100"
            />
            <item.icon className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-accent-foreground group-data-[status=active]:text-primary" />
            {t(item.labelKey)}
          </Link>
        ))}
      </nav>

      <div className="border-t border-border pt-3 mt-3 space-y-2">
        {me.data && (
          <div className="rounded-md px-2 py-2">
            <div className="text-xs font-medium text-foreground truncate">
              {me.data.full_name || me.data.username}
            </div>
            <div className="text-[10px] text-muted-foreground truncate">
              {me.data.merchant.name}
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              <Badge variant="secondary" className="text-[10px] uppercase">
                {me.data.role}
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {me.data.merchant.code}
              </Badge>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 px-2">
          <Globe className="h-4 w-4 text-muted-foreground" aria-label={t('common.language')} />
          <Select
            value={i18n.language.split('-')[0]}
            onValueChange={(v) => i18n.changeLanguage(v as LangCode)}
          >
            <SelectTrigger className="h-8 flex-1 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUPPORTED.map((l) => (
                <SelectItem key={l.code} value={l.code}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start"
          onClick={onLogout}
        >
          <LogOut className="h-4 w-4" />
          {t('common.sign_out')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh bg-background lg:grid lg:grid-cols-[260px_1fr]">
      <header className="lg:hidden sticky top-0 z-30 flex items-center gap-2 border-b border-border bg-card/90 backdrop-blur px-3 py-2">
        <Sheet open={navOpen} onOpenChange={setNavOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="sm" aria-label="Open navigation">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="start" className="app-sidebar">
            <SheetTitle>{t('nav.dashboard')}</SheetTitle>
            <SheetDescription>{t('common.language')}</SheetDescription>
            {sidebar}
          </SheetContent>
        </Sheet>
        <Wordmark size="sm" />
      </header>

      <aside className="app-sidebar hidden lg:flex flex-col p-4">{sidebar}</aside>
      <main className="overflow-x-hidden">
        <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
